import { Node, mergeAttributes } from '@tiptap/core';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type ReactNodeViewRendererOptions,
} from '@tiptap/react';
import dynamic from 'next/dynamic';
import { useCallback, useRef, useState } from 'react';

// Dynamically import ReactPlayer to avoid SSR issues
const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

const VideoComponent = ({ node }: { node: Partial<ReactNodeViewRendererOptions> }) => {
  const videoUrl = (node?.attrs as any)?.src;
  const playerRef = useRef<any>(null);
  const [hasEndedAtCap, setHasEndedAtCap] = useState(false);
  const MAX_SECONDS = 90;

  const handleProgress = useCallback(
    (state: { played: number; playedSeconds: number }) => {
      if (state.playedSeconds >= MAX_SECONDS && !hasEndedAtCap) {
        setHasEndedAtCap(true);
        if (playerRef.current) {
          // Pause and seek to the cap boundary
          try {
            playerRef.current.seekTo(MAX_SECONDS, 'seconds');
          } catch {}
        }
      }
    },
    [hasEndedAtCap],
  );

  const handleSeek = useCallback((seconds: number) => {
    if (seconds > MAX_SECONDS && playerRef.current) {
      try {
        playerRef.current.seekTo(MAX_SECONDS, 'seconds');
      } catch {}
    }
  }, []);

  if (!videoUrl) {
    return (
      <NodeViewWrapper>
        <div
          data-video-url={videoUrl}
          className="flex items-center justify-center p-8 bg-gray-100 rounded-lg text-red-500">
          No video URL provided
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper>
      <div
        data-video-url={videoUrl}
        className="relative w-full max-w-full my-4 rounded-lg overflow-hidden bg-black"
        style={{ aspectRatio: '16/9' }}>
        <ReactPlayer
          ref={playerRef}
          url={videoUrl}
          controls
          width="100%"
          height="100%"
          style={{ position: 'absolute', top: 0, left: 0 }}
          onProgress={handleProgress}
          onSeek={handleSeek}
          playing={!hasEndedAtCap}
          config={{
            file: {
              attributes: {
                controlsList: 'nodownload',
                playsInline: true,
              },
            },
          }}
        />
      </div>
    </NodeViewWrapper>
  );
};

export interface VideoOptions {
  HTMLAttributes: Record<string, any>;
  inline: boolean;
}

type SetVideoOptions = { src: string };

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    video: {
      setVideo: (options: SetVideoOptions) => ReturnType;
    };
  }
}

export const Video = Node.create<VideoOptions>({
  name: 'video',

  addOptions() {
    return {
      HTMLAttributes: {},
      inline: false,
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(VideoComponent, { attrs: this.options.HTMLAttributes });
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-video-url]',
      },
    ];
  },

  addCommands() {
    return {
      setVideo:
        (options: SetVideoOptions) =>
        ({ commands }) => {
          if (!options.src) {
            return false;
          }

          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },

  renderHTML({ HTMLAttributes, node }) {
    return ['div', mergeAttributes({ 'data-video-url': node.attrs.src }, HTMLAttributes)];
  },
});
